// localAgentConstants.js
// Aligned with AmploFly premium glassmorphism theme and Local Agent specifications

export const MODULE_FPM = 'fpm';
export const MODULE_RISK_REGISTER = 'risk_register';
export const MODULE_KPI = 'kpi';
export const MODULE_CAPABILITY_CELL = 'capability_cell';
export const MODULE_KPI_ASSISTANT = 'kpi_assistant';
export const MODULE_KPI_SETUP = 'kpi_setup';
export const MODULE_RISK_FULL_ANALYSIS = 'risk_full_analysis';

export const SEVERITY_COLORS = {
    severe: '#ef4444', // Red
    high: '#f97316',   // Orange
    medium: '#eab308', // Yellow
    low: '#22c55e',    // Green
};

export const CONFIDENCE_LEVELS = {
    high: { label: 'High Confidence', color: '#22c55e', score: '92%' },
    medium: { label: 'Medium Confidence', color: '#3b82f6', score: '78%' },
    low: { label: 'Low Confidence', color: '#f97316', score: '54%' },
};

export const FPM_SCENARIOS = [
    {
        id: 's1_1_analyze_fpm',
        label: 'Analyze the FPM and recommend the high priority weak capabilities',
        description: 'PRIORITIZATION',
        icon: '⚠️',
        reasoningSteps: ['Scan all weak cells', 'Analyze severity impacts', 'Recommend priority capabilities'],
    },
    {
        id: 's1_2_diagnose_cluster',
        label: 'Diagnose the cluster of weakness',
        description: 'ANOMALY DETECTION',
        icon: '🔍',
        reasoningSteps: ['Identify weak clusters', 'Analyze dependencies', 'Diagnose root cause'],
    },
    {
        id: 's1_3_surface_vs_structural',
        label: 'Surface vs structural — where is the weakness deep',
        description: 'DEEP ANALYSIS',
        icon: '📉',
        reasoningSteps: ['Analyze node hierarchy', 'Check structural vs surface weakness', 'Formulate explanation'],
    },
    {
        id: 's1_4_find_quick_wins',
        label: 'Find the quick wins',
        description: 'ACTION PLAN',
        icon: '💡',
        reasoningSteps: ['Scan for high-impact, low-effort improvements', 'Draft recommendations'],
    },
    {
        id: 's1_5_compare_functions',
        label: 'Compare functions side by side',
        description: 'COMPARISON',
        icon: '⚖️',
        reasoningSteps: ['Align functions side by side', 'Identify relative strengths and weaknesses'],
    },
    {
        id: 's1_6_executive_summary',
        label: 'Generate executive summary',
        description: 'SYNTHESIS',
        icon: '📊',
        reasoningSteps: ['Compile findings across heatmap', 'Format for executive review'],
    }
];

export const FPM_QUICK_QUESTIONS = [
    { id: 'fpm_qq_1', label: 'Which capabilities should I prioritize first?', description: 'RECOMMENDATION', icon: '❓', reasoningSteps: ['Evaluating priorities'] },
    { id: 'fpm_qq_2', label: 'Which weak areas should move to Pain Point Initiative?', description: 'ANALYSIS', icon: '❓', reasoningSteps: ['Checking pain points'] },
    { id: 'fpm_qq_3', label: 'Which weak areas are likely data-quality problems?', description: 'DIAGNOSIS', icon: '❓', reasoningSteps: ['Checking data quality'] }
];

export const RISK_REGISTER_SCENARIOS = [
    {
        id: 's2_1_draft_rca_solution',
        label: 'S2.1. Draft RCA + Solution preview',
        description: 'Synthesize evidence into a Root Cause and actionable Solution',
        icon: '📝',
        reasoningSteps: [
            'Read full L1-L5 tree and scoring attributes for capability',
            'Check framework mappings for regulatory framing',
            'Check if capability has prior Pain Point Initiative history',
            'Draft RCA and Solution preserving evidence trail',
        ],
    },
    {
        id: 's2_2_interlock_map',
        label: 'S2.2. Capability interlock map',
        description: 'Find other capabilities structurally connected to this weakness',
        icon: '🔗',
        reasoningSteps: [
            'Identify weak L1-L5 sub-nodes for this capability',
            'Search across all other capabilities for shared sub-nodes',
            'Compute secondary lift potential for connected capabilities',
            'Map the non-obvious leverage network',
        ],
    },
];

export const CAPABILITY_CELL_SCENARIOS = [
    {
        id: 's2_1_drill_down',
        label: 'Explain why this capability is weak',
        description: 'DIAGNOSIS',
        icon: '🔍',
        reasoningSteps: ['Extract node scores', 'Analyze historical trends', 'Formulate explanation']
    }
];

export const KPI_SCENARIOS = [
    {
        id: 's4_1_recommend_kpis',
        label: 'S4.1. Recommend aligned KPIs',
        description: 'Check KPI alignment with RCA, data templates, and framework coverage',
        icon: '🎯',
        reasoningSteps: [
            'Analyze RCA and target objectives',
            'Cross-reference with existing data templates',
            'Check overlap with already-tracked Performance KPIs',
            'Recommend structured KPI candidates for import',
        ],
    },
    {
        id: 's5_1_suggest_baseline_target',
        label: 'S5.1. Suggest Baseline & Targets',
        description: 'Anchor targets in historical data, fixability, and metric math',
        icon: '📈',
        reasoningSteps: [
            'Analyze underlying capability score gap and L1-L5 fixability',
            'Anchor against company historical data',
            'Check mathematical viability of the formula',
            'Draft baseline and phased target recommendations',
        ],
    },
];

export const KPI_ASSISTANT_SCENARIOS = [
    {
        id: 'kpi_1_alignment_check',
        label: 'Alignment check — do these KPIs measure the RCA?',
        description: 'ALIGNMENT',
        icon: '🎯',
        reasoningSteps: ['Extract RCA text', 'Analyze KPI intent', 'Compare conceptual overlap']
    },
    {
        id: 'kpi_2_rank_kpis',
        label: 'Rank the KPI’s',
        description: 'PRIORITIZATION',
        icon: '📊',
        reasoningSteps: ['Analyze business impact', 'Determine priority score']
    },
    {
        id: 'kpi_3_leading_lagging',
        label: 'Check whether KPI’s are leading or lagging',
        description: 'METRIC TYPING',
        icon: '⏱️',
        reasoningSteps: ['Analyze metric timeframe', 'Classify as leading or lagging']
    },
    {
        id: 'kpi_4_actionability',
        label: 'Check KPI actionability',
        description: 'EVALUATION',
        icon: '✅',
        reasoningSteps: ['Assess control levers', 'Determine actionability']
    },
    {
        id: 'kpi_5_polarity',
        label: 'Check KPI polarity',
        description: 'DIRECTIONALITY',
        icon: '↕️',
        reasoningSteps: ['Analyze desired outcome direction', 'Determine positive or negative polarity']
    }
];

export const KPI_SETUP_SCENARIOS = [
    {
        id: 'kpi_setup_1_baseline',
        label: 'Suggested baseline value',
        description: 'TOOL USE',
        icon: '📊',
        reasoningSteps: ['Analyze historical metrics', 'Determine peer benchmarks', 'Calculate suggested baseline']
    },
    {
        id: 'kpi_setup_2_target',
        label: 'Suggested target value and trajectory',
        description: 'MULTI-STEP + TOOL USE',
        icon: '📈',
        reasoningSteps: ['Evaluate baseline context', 'Analyze improvement curve', 'Determine target trajectory']
    },
    {
        id: 'kpi_setup_3_sanity',
        label: 'Sanity check this configuration',
        description: 'ANOMALY DETECTION',
        icon: '🔍',
        reasoningSteps: ['Analyze metric bounds', 'Cross-reference dependencies', 'Identify structural anomalies']
    }
];

export const RISK_FULL_ANALYSIS_SCENARIOS = [
    {
        id: 'generate_all',
        label: 'Generate Full Analysis (RCA & Solution)',
        description: 'SYNTHESIS',
        icon: '📝',
        reasoningSteps: ['Read L1-L5 tree', 'Draft Root Cause', 'Draft Actionable Solution']
    },
    {
        id: 'rca_only',
        label: 'Draft Root Cause Only',
        description: 'DIAGNOSIS',
        icon: '🔍',
        reasoningSteps: ['Analyze capability gaps', 'Draft RCA preserving evidence']
    },
    {
        id: 'solution_only',
        label: 'Draft Solution Only',
        description: 'ACTION PLAN',
        icon: '💡',
        reasoningSteps: ['Analyze existing RCA', 'Draft structured mitigation steps']
    }
];

export const UI_TEXT = {
    engineReady: 'AI Engine Ready',
    analyzing: 'Analyzing Context...',
    analysisComplete: 'Analysis Complete',
    contextSync: 'Context Synchronized',
    scenariosHeader: 'Analysis Scenarios',
    reasoningPathHeader: 'Reasoning Path',
    followUpHeader: 'Guided Follow-Up',
    followUpPlaceholder: 'Ask a contextual follow-up question...',
    errorTitle: 'Analysis Interrupted',
    errorMessage: 'An unexpected issue occurred while synthesizing the data. Please try again.',
    retryBtn: 'Retry Analysis',
    backBtn: 'Back to Scenarios',
    noDataTitle: 'Empty Context Space',
    noDataMessage: 'No active capability or matrix data detected on screen. Please ensure the workspace contains data first.',
    advisoryHeader: '✨ AI Advisory & Benchmark Analysis',
    suggestionHeader: '✨ AI Context Suggestion',
    useThis: 'Use This',
    appendSelected: 'Append Selected',
    dismiss: 'Dismiss',
    regenerate: 'Regenerate',
};

export const getScenariosByModule = (moduleKey) => {
    switch (moduleKey) {
        case MODULE_FPM:
            return FPM_SCENARIOS;
        case MODULE_RISK_REGISTER:
            return RISK_REGISTER_SCENARIOS;
        case MODULE_RISK_FULL_ANALYSIS:
            return RISK_FULL_ANALYSIS_SCENARIOS;
        case MODULE_KPI:
            return KPI_SCENARIOS;
        case MODULE_CAPABILITY_CELL:
            return CAPABILITY_CELL_SCENARIOS;
        case MODULE_KPI_ASSISTANT:
            return KPI_ASSISTANT_SCENARIOS;
        case MODULE_KPI_SETUP:
            return KPI_SETUP_SCENARIOS;
        default:
            return [];
    }
};
